import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext, Token } from "../contexts/authContext";
import { DataRefreshContext } from "../contexts/dataRefreshContext";
import {
  EnvironmentConfig,
  EnvironmentContext,
} from "../contexts/environmentContext";
import { useSetting } from "../util";
import { PagedResponse } from "./models";

type ApiGetter<T> = (
  token: Token,
  environment: EnvironmentConfig,
  query?: string
) => Promise<T>;

const defaultDependency = {};

export interface ApiState<T> {
  value: T;
  loading: boolean;
  failed: boolean;
  lastLoadedAt?: Date;
  refresh(): Promise<void>;
}

export interface PagedApiState<T>
  extends ApiState<PagedResponse<T> | undefined> {
  currentPage: number;
  currentPageSize: number;
  goToPage(page: number): void;
  setPageSize(pageSize: number): void;
}

export interface ApiErrorResponse {
  statusCode: number;
  detail: object;
  data?: any;
}

export interface UpdateResult {
  error: boolean;
  response: ApiErrorResponse;
}

export function useApiState<T>(
  get: ApiGetter<T>,
  defaultValue: T,
  dependency?: any
): ApiState<T> {
  const [value, set] = useState<T>(defaultValue);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const isMounted = useRef(true);

  const [lastLoadedAt, setLastLoadedAt] = useState<Date | undefined>();
  const authContext = useContext(AuthContext);
  const environmentContext = useContext(EnvironmentContext);
  const dataRefreshContext = useContext(DataRefreshContext);

  if (!authContext.currentUser) {
    throw new Error(
      "useApiState should not be called before the user is authenticated"
    );
  }

  const dependencyList = dependency
    ? Array.isArray(dependency)
      ? dependency
      : [dependency]
    : [defaultDependency];

  dependencyList.push(dataRefreshContext.refreshCounter);

  const refresh = useCallback(async () => {
    if (!isMounted.current) return;
    setLoading(true);
    setFailed(false);
    try {
      const result = await get(
        authContext.currentUser!.accessToken,
        environmentContext
      );

      if (!isMounted.current) return;

      if (result && (result as any).statusCode) {
        setFailed(true);
        return;
      }

      set(result);
      setLastLoadedAt(new Date());
    } catch {
      if (isMounted.current) setFailed(true);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [get, set, setLoading, authContext, environmentContext]);

  useEffect(() => {
    isMounted.current = true;
    refresh();
    return () => {
      isMounted.current = false;
    };
  }, [...dependencyList, refresh]);

  return { value, loading, failed, lastLoadedAt, refresh };
}

export function usePagedApiState<T>(
  get: ApiGetter<PagedResponse<T>>,
  dependency?: any,
  pageSize?: number
): PagedApiState<T> {
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useSetting(
    "default-page-size",
    50
  );
  const effectivePageSize = pageSize === undefined ? currentPageSize : pageSize;

  const dependencyList = dependency
    ? Array.isArray(dependency)
      ? dependency
      : [dependency]
    : [defaultDependency];

  dependencyList.push(currentPage);
  dependencyList.push(currentPageSize);
  let offsetText = "offset";
  let limitText = "limit";

  const getter = useCallback(
    (t, e) =>
      get(
        t,
        e,
        `${limitText}=${effectivePageSize}&${offsetText}=${
          effectivePageSize * currentPage
        }`
      ),
    [effectivePageSize, currentPage, get]
  );
  const state: any = useApiState(getter, undefined, dependencyList);

  if (state.value?.hasOwnProperty("itemsReturned")) {
    state.value["totalCount"] = state.value["totalItemsFromQuery"];
  }

  return {
    currentPage,
    currentPageSize: effectivePageSize,
    goToPage: (p) => setCurrentPage(p),
    setPageSize: (pageSize) => setCurrentPageSize(pageSize),
    ...state,
  };
}

export function useGetAuthorized<T>(
  url: string,
  postProcessor?: (t: T) => T,
  manipulateUrl?: (url: string) => string,
  abortFetch?: boolean,
  serverType?: "integration-platform" | "sensors",
  responseType?: "json" | "text"
): ApiGetter<T> {
  return useCallback(
    async (token, environment, query) => {
      if (abortFetch) {
        return {} as T;
      }

      await token.refreshIfExpired();

      let effectiveUrl = url;
      if (query) {
        effectiveUrl =
          effectiveUrl + (effectiveUrl.indexOf("?") >= 0 ? "&" : "?") + query;
      }

      let baseUrl: string | undefined = "";
      if (serverType === "integration-platform") {
        baseUrl = environment.api.baseUrlIntPlatform;
      } else {
        baseUrl = environment.api.baseUrlv2;
      }

      if (serverType === "integration-platform") {
        effectiveUrl = baseUrl + effectiveUrl;
      } else {
        effectiveUrl = baseUrl + "api/" + effectiveUrl;
      }

      if (manipulateUrl) {
        effectiveUrl = manipulateUrl(effectiveUrl);
      }

      let response = await fetch(effectiveUrl, {
        method: "get",
        headers: new Headers({
          Authorization: "Bearer " + token.jwt,
        }),
      });

      if (responseType === "text") {
        let text = await response.text();

        return text as unknown as T;
      } else {
        let json = (await response.json()) as T;

        if (postProcessor) {
          json = postProcessor(json);
        }

        return json;
      }
    },
    manipulateUrl ? [url, manipulateUrl] : [url]
  );
}

export interface ApiCall<TInput, TResponse> {
  (input: TInput): Promise<TResponse>;
}

export function useAuthorizedFetch(
  jsonBody: boolean = true,
  serverType?: "integration-platform" | "sensors"
) {
  const authContext = useContext(AuthContext);
  const environmentContext = useContext(EnvironmentContext);
  return useCallback(
    async (url: string, init?: RequestInit) => {
      const token = authContext.currentUser!.accessToken;
      await token.refreshIfExpired();

      const headers = new Headers({
        ...(init?.headers || {}),
        Authorization: "Bearer " + token.jwt,
        ...(jsonBody
          ? { "Content-Type": "application/json; charset=utf-8" }
          : {}),
      });

      let effectiveUrl = "";

      if (serverType === "integration-platform") {
        effectiveUrl = environmentContext?.api?.baseUrlIntPlatform + url;
      } else {
        effectiveUrl = environmentContext?.api?.baseUrlv2 + "api/" + url;
      }

      return fetch(effectiveUrl, {
        ...(init || {}),
        headers,
      });
    },
    [authContext, environmentContext, jsonBody]
  );
}
